// A list row's verbs behind one "…" button (consistency-pass spec §4),
// ported from the vouchers admin (lib/chassis/row-actions-menu.tsx and
// RowActionDescriptor in lib/crud/types.ts) without its soft-delete rules:
// nothing here is soft-deleted. Ordering contract: descriptors arrive
// navigation first, danger last; the separator goes before the first danger
// item, which renders red. An action that can't run stays in the menu,
// disabled, with its reason underneath — the same reason the page's own
// danger zone gives.

import { Button, DropdownMenu } from "@pej/kore/components";
import { Link, type LinkProps } from "@tanstack/react-router";
import { MoreHorizontal } from "lucide-react";
import { type ComponentType, Fragment } from "react";

export interface RowActionDescriptor<E> {
    /** Stable id; React key. */
    id: string;
    label: string;
    icon: ComponentType<{ className?: string }>;
    /** "danger" renders last, after a separator, in red. */
    section?: "default" | "danger";
    /** Navigation renders a real <Link> (cmd-click opens a tab). Exclusive with onClick. */
    linkProps?: (entity: E) => LinkProps;
    onClick?: (entity: E) => void;
    /** A short reason when the action can't run for this entity; undefined when it can. */
    disabledReason?: (entity: E) => string | undefined;
}

export function RowActionsMenu<E>({
    actions,
    entity,
    label = "Row actions",
}: {
    actions: RowActionDescriptor<E>[];
    entity: E;
    /** The trigger's accessible name, per row ("Actions for Lunch feedback")
     *  so a screen reader doesn't hear the same button on every row. */
    label?: string;
}) {
    if (actions.length === 0) return null;
    const firstDanger = actions.findIndex((action) => action.section === "danger");
    return (
        // The row opens its item on click and Enter, and React events from the
        // menu's portal still bubble through this tree: stopped here, so a
        // menu choice never also opens the row.
        // biome-ignore lint/a11y/noStaticElementInteractions: event boundary only; the button and menu items inside are the interactive elements
        <span
            role="presentation"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
        >
            <DropdownMenu>
                <DropdownMenu.Trigger render={<Button variant="ghost" size="icon" aria-label={label} />}>
                    <MoreHorizontal />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" className="w-64">
                    {actions.map((action, index) => {
                        const reason = action.disabledReason?.(entity);
                        const danger = action.section === "danger";
                        const item = (
                            <DropdownMenu.Item
                                key={action.id}
                                disabled={!!reason}
                                variant={danger ? "destructive" : "default"}
                                onClick={reason || action.linkProps ? undefined : () => action.onClick?.(entity)}
                                render={
                                    action.linkProps && !reason ? <Link {...action.linkProps(entity)} /> : undefined
                                }
                            >
                                <action.icon className="size-4" />
                                <span className="flex min-w-0 flex-col">
                                    <span>{action.label}</span>
                                    {reason && <span className="text-xs text-kore-text-muted">{reason}</span>}
                                </span>
                            </DropdownMenu.Item>
                        );
                        return index === firstDanger && index > 0 ? (
                            <Fragment key={action.id}>
                                <DropdownMenu.Separator />
                                {item}
                            </Fragment>
                        ) : (
                            item
                        );
                    })}
                </DropdownMenu.Content>
            </DropdownMenu>
        </span>
    );
}
